// @givmo/org-console — the share kit card (CPB-WEB3 subset; Email Campaign plan §2.1, D-EMC-11).
//
// Built once and rented (Jonathan, 2026-10-27: "a share kit isn't its own module right? … we can
// have access to the same tools from multiple modules"): the Board Ask page, a person's fundraiser
// page and the campaign editor all draw this card. Its layout is Jonathan's approved walkthrough
// (#26, #31): Your link with Copy, the QR with "Download QR code", Captions each with Copy,
// Graphics each with its own download and "Download all", and, on a person's card, "Email your
// contacts", which opens their own mail app (a `mailto:`), as its hint says — Givmo never mails a
// person's contacts (plan §2.8). The
// organization's card has no such button: it mails through its Emails tab.
//
// A person's card shows their ONE link. Before they have a fundraiser page under the campaign it
// is "Share the campaign"; once their page is live the SAME link opens it and the card reads
// "Share your page" (`link_points_to`), so a renter passes no title. Every word and figure on it is
// the server's: the captions name only the organization and the page.

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  type ShareKitEntityTypeParam,
  type ShareKitOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  IconButton,
  Skeleton,
  cn,
  saasButtonVariants,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Copy, Download, Mail, Palette, QrCode } from 'lucide-react';
import {
  SHARE_KIT_NO_CREDIT,
  displayLink,
  downloadShareKitGraphics,
  downloadShareKitQr,
  shareKitMailto,
  shareKitOptions,
  shareKitQrOptions,
  type ShareKitQrFormat,
  type ShareKitSource,
} from './share-kit-queries';

export interface ShareKitProps {
  /** The page shared: a fundraiser (a campaign, or the person's own page) or an organization's own page. */
  entityType: ShareKitEntityTypeParam;
  entityId: string;
  /** The graphics the renter chose (the Board Ask's attachments). Out-of-date ones never show. */
  collateralItemIds?: readonly string[];
  /**
   * The organization's own kit (the campaign editor's card): its canonical link, in its voice, with
   * its library's current graphics. Only for a fundraiser.
   */
  orgRef?: string;
  /** Opens the organization's QR design editor (the organization's kit only). */
  onCustomizeQr?: () => void;
  /** No card chrome: a heading and the kit, for a renter's own card (walkthrough #31). */
  bare?: boolean;
  /**
   * What to draw while the kit is switched off here (its route miss). Nothing by default; the
   * campaign editor keeps its share panel until the kit serves everywhere.
   */
  fallback?: ReactNode;
  /**
   * Called after each sharing act succeeds (a copy, a download, opening the email), so a renter can
   * record that the person shared (the Board Ask's "Who's taking part").
   */
  onShared?: (how: ShareKitAct) => void;
  /**
   * What the card says where the kit carries no graphics, in their place: a host's pointer to where
   * they are chosen (EMC-BE23: the campaign editor's "Pick them in Graphics to share."). Nothing by
   * default, so every other host's card is as before.
   */
  noGraphics?: ReactNode;
  /**
   * What the organization's page is, where it isn't a plain fundraiser (a game's page: "game"), so the card
   * names it so. FCM-WEB35's production fix 14 (D-FCM-77), one name for a game's page.
   */
  noun?: string;
  testId?: string;
}

/** The sharing acts the card reports through `onShared`. */
export type ShareKitAct =
  | 'copy_link'
  | 'copy_caption'
  | 'download_qr'
  | 'download_graphics'
  | 'email';

const COPY = {
  campaignTitle: 'Share the campaign',
  campaignSubtitle: 'Gifts through your link are counted as yours.',
  ownPageTitle: 'Share your page',
  profileTitle: 'Share',
  organizationTitle: 'Share',
  organizationSubtitle: 'Send supporters to your fundraiser page.',
  yourLink: 'Your link',
  link: 'Short link',
  pageLink: 'Fundraiser page',
  downloadQr: 'Download QR code',
  captions: 'Captions',
  graphics: 'Graphics',
  downloadAll: 'Download all',
  download: 'Download',
  email: 'Email your contacts',
  emailHint: 'Opens your own email. Givmo doesn’t send it.',
  loadFailed: 'Couldn’t load your share kit.',
  creditUnavailable:
    'We can’t give you a link right now, because gifts through it couldn’t be counted as yours. Please try again later.',
  tryAgain: 'Try again',
} as const;

/**
 * The card's test ids. The organization's card keeps the ids the campaign editor's share panel had
 * (`share-panel`, `share-public-url`, `share-short-link`, `share-qr-svg|png|pdf`,
 * `share-qr-customize`), which the e2e suite reads; a person's card derives them from `testId`.
 */
export function shareKitTestIds(testId: string, organization: boolean) {
  const p = organization ? 'share' : testId;
  return {
    root: organization ? 'share-panel' : testId,
    loading: `${p}-loading`,
    error: `${p}-error`,
    creditUnavailable: `${p}-credit-unavailable`,
    retry: `${p}-retry`,
    title: `${p}-title`,
    header: `${p}-header`,
    pageUrl: `${p}-public-url`,
    link: `${p}-link`,
    copyLink: organization ? 'share-short-link' : `${p}-copy-link`,
    qrImage: `${p}-qr-image`,
    qrPng: organization ? 'share-qr-png' : `${p}-download-qr`,
    qrSvg: `${p}-qr-svg`,
    qrPdf: `${p}-qr-pdf`,
    customize: `${p}-qr-customize`,
    captions: `${p}-captions`,
    caption: (index: number) => `${p}-caption-${index}`,
    copyCaption: (index: number) => `${p}-copy-caption-${index}`,
    graphics: `${p}-graphics`,
    graphic: (id: string) => `${p}-graphic-${id}`,
    downloadGraphics: `${p}-download-graphics`,
    email: `${p}-email`,
    emailHint: `${p}-email-hint`,
  };
}

type KitTestIds = ReturnType<typeof shareKitTestIds>;

function headerFor(
  kit: ShareKitOut | undefined,
  organization: boolean,
  noCredit: boolean,
  noun?: string,
) {
  if (organization)
    return {
      title: COPY.organizationTitle,
      subtitle: noun ? `Send supporters to this ${noun}’s page.` : COPY.organizationSubtitle,
    };
  if (noCredit) return { title: COPY.campaignTitle, subtitle: undefined };
  if (kit?.link_points_to === 'own_page') return { title: COPY.ownPageTitle, subtitle: undefined };
  if (kit?.link_points_to === 'profile') {
    return { title: COPY.profileTitle, subtitle: COPY.campaignSubtitle };
  }
  return { title: COPY.campaignTitle, subtitle: COPY.campaignSubtitle };
}

async function copyText(value: string, what: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
    return true;
  } catch {
    toast.error(`Couldn’t copy the ${what.toLowerCase()}. Copy it by hand instead.`);
    return false;
  }
}

export function ShareKit({
  entityType,
  entityId,
  collateralItemIds = [],
  orgRef,
  onCustomizeQr,
  bare = false,
  fallback = null,
  onShared,
  noGraphics = null,
  noun,
  testId = 'share-kit',
}: ShareKitProps) {
  const organization = orgRef !== undefined;
  const source: ShareKitSource = organization
    ? { kind: 'organization', orgRef, fundraiserId: entityId }
    : { kind: 'person', entityType, entityId, collateralItemIds };
  const kit = useQuery(shareKitOptions(source));
  const qr = useQuery({ ...shareKitQrOptions(source), enabled: kit.isSuccess });
  const [busy, setBusy] = useState<ShareKitQrFormat | 'graphics' | null>(null);

  // A route miss is the kit being absent here (switched off): the host's fallback, never a broken card.
  if (isGivmoError(kit.error) && kit.error.status === 404) return <>{fallback}</>;

  const noCredit = isGivmoError(kit.error) && firstErrorCode(kit.error) === SHARE_KIT_NO_CREDIT;
  const header = headerFor(kit.data, organization, noCredit, noun);
  const ids = shareKitTestIds(testId, organization);

  async function downloadQr(format: ShareKitQrFormat) {
    setBusy(format);
    try {
      await downloadShareKitQr(source, format);
      onShared?.('download_qr');
    } catch {
      toast.error('Couldn’t download the QR code. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  async function downloadGraphics(data: ShareKitOut) {
    setBusy('graphics');
    try {
      await downloadShareKitGraphics(
        source.kind === 'person'
          ? { ...source, collateralItemIds: data.graphics.map((graphic) => graphic.id) }
          : source,
      );
      onShared?.('download_graphics');
    } catch {
      toast.error('Couldn’t download the graphics. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  const body = kit.isPending ? (
    <div className="flex flex-col gap-12" {...testIdProp(ids.loading)}>
      <Skeleton className="h-[120px] w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  ) : noCredit ? (
    <p className="text-s13 text-text-secondary" {...testIdProp(ids.creditUnavailable)}>
      {COPY.creditUnavailable}
    </p>
  ) : kit.isError || !kit.data ? (
    <div className="flex flex-wrap items-center gap-8" {...testIdProp(ids.error)}>
      <p className="text-s13 text-text-secondary">{COPY.loadFailed}</p>
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={() => void kit.refetch()}
        testId={ids.retry}
      >
        {COPY.tryAgain}
      </Button>
    </div>
  ) : (
    <KitBody
      kit={kit.data}
      qrSrc={qr.data}
      qrFailed={qr.isError}
      organization={organization}
      noun={noun}
      busy={busy}
      onDownloadQr={downloadQr}
      onDownloadGraphics={() => void downloadGraphics(kit.data)}
      onCustomizeQr={onCustomizeQr}
      onShared={onShared}
      noGraphics={noGraphics}
      ids={ids}
    />
  );

  if (bare) {
    return (
      <section className="flex flex-col gap-16" {...testIdProp(ids.root)}>
        <h3 className="text-s16 font-w600 text-text-strong" {...testIdProp(ids.title)}>
          {header.title}
        </h3>
        {body}
      </section>
    );
  }
  return (
    <Card testId={ids.root}>
      <CardHeader title={header.title} subtitle={header.subtitle} testId={ids.header} />
      <CardBody>{body}</CardBody>
    </Card>
  );
}

interface KitBodyProps {
  kit: ShareKitOut;
  qrSrc: string | undefined;
  qrFailed: boolean;
  organization: boolean;
  busy: ShareKitQrFormat | 'graphics' | null;
  onDownloadQr: (format: ShareKitQrFormat) => void;
  onDownloadGraphics: () => void;
  onCustomizeQr?: () => void;
  onShared?: (how: ShareKitAct) => void;
  noGraphics: ReactNode;
  noun?: string;
  ids: KitTestIds;
}

function KitBody({
  kit,
  qrSrc,
  qrFailed,
  organization,
  busy,
  onDownloadQr,
  onDownloadGraphics,
  onCustomizeQr,
  onShared,
  noGraphics,
  noun,
  ids,
}: KitBodyProps) {
  const copy = async (value: string, what: string, how: ShareKitAct) => {
    if (await copyText(value, what)) onShared?.(how);
  };
  const link = displayLink(kit.share_url);
  return (
    <div className="@container flex flex-col gap-16">
      {organization && (
        <div className="flex flex-col gap-8">
          <span className="text-s13 font-w600 text-text-strong">
            {noun ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} page` : COPY.pageLink}
          </span>
          <div className="flex items-center gap-8">
            <span
              className="min-w-0 flex-1 truncate rounded-md bg-surface-sunken px-10 py-8 text-s13 text-text-strong"
              title={kit.target.page_url}
            >
              {displayLink(kit.target.page_url)}
            </span>
            <CopyButton
              label={`Copy the ${noun ?? 'fundraiser'} page link`}
              onClick={() =>
                void copy(
                  kit.target.page_url,
                  noun
                    ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} page link`
                    : 'Fundraiser page link',
                  'copy_link',
                )
              }
              testId={ids.pageUrl}
            />
          </div>
        </div>
      )}
      {/* The QR beside the link, as walkthrough #26 draws it. FCM-WEB35's production fix 18 (D-FCM-77): stacked,
          the link's label comes first, so the QR sits under the label it belongs to, not under the page's
          link above it; side by side, the QR spans the label's row and the link's. */}
      <div className="grid grid-cols-1 items-start gap-x-14 gap-y-8 @[380px]:grid-cols-[88px_minmax(0,1fr)] @[380px]:gap-y-4">
        <span className="order-1 text-s13 font-w600 text-text-strong @[380px]:col-start-2 @[380px]:row-start-1">
          {organization ? COPY.link : COPY.yourLink}
        </span>
        <div className="order-2 w-[88px] rounded-md border border-border-subtle bg-surface p-2 @[380px]:col-start-1 @[380px]:row-span-2 @[380px]:row-start-1">
          {qrSrc ? (
            <img
              src={qrSrc}
              alt="QR code for your link"
              className="block h-auto w-full"
              {...testIdProp(ids.qrImage)}
            />
          ) : qrFailed ? (
            <span
              aria-hidden
              className="flex aspect-square w-full items-center justify-center text-icon-muted"
            >
              <Icon icon={QrCode} size={24} />
            </span>
          ) : (
            <Skeleton className="aspect-square w-full" />
          )}
        </div>
        <div className="order-3 flex min-w-0 flex-col gap-4 @[380px]:col-start-2 @[380px]:row-start-2">
          <div className="flex items-center gap-8">
            <span
              className="min-w-0 flex-1 truncate rounded-md bg-surface-sunken px-10 py-8 text-s13 text-text-strong"
              title={kit.share_url}
              {...testIdProp(ids.link)}
            >
              {link}
            </span>
            <CopyButton
              label="Copy your link"
              onClick={() => void copy(kit.share_url, 'Link', 'copy_link')}
              testId={ids.copyLink}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-16 gap-y-4">
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
              loading={busy === 'png'}
              disabled={busy !== null && busy !== 'png'}
              onClick={() => onDownloadQr('png')}
              testId={ids.qrPng}
            >
              {COPY.downloadQr}
            </Button>
            {organization && (
              <>
                {(['svg', 'pdf'] as const).map((format) => (
                  <Button
                    key={format}
                    appearance="saas"
                    tone="link"
                    size="sm"
                    loading={busy === format}
                    disabled={busy !== null && busy !== format}
                    onClick={() => onDownloadQr(format)}
                    testId={format === 'svg' ? ids.qrSvg : ids.qrPdf}
                  >
                    {format.toUpperCase()}
                  </Button>
                ))}
                {onCustomizeQr && (
                  <Button
                    appearance="saas"
                    tone="link"
                    size="sm"
                    leadingIcon={<Icon icon={Palette} size={14} aria-hidden />}
                    onClick={onCustomizeQr}
                    testId={ids.customize}
                  >
                    Customize QR code
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {kit.captions.length > 0 && (
        <div className="flex flex-col gap-8" {...testIdProp(ids.captions)}>
          <span className="text-s13 font-w600 text-text-strong">{COPY.captions}</span>
          {kit.captions.map((caption, index) => (
            <div key={caption.key} className="flex items-start gap-8">
              <p
                className="min-w-0 flex-1 rounded-md bg-surface-sunken px-10 py-8 text-s13 leading-[18px] text-text-strong"
                {...testIdProp(ids.caption(index))}
              >
                {caption.text}
              </p>
              <CopyButton
                label="Copy this caption"
                onClick={() => void copy(caption.text, 'Caption', 'copy_caption')}
                testId={ids.copyCaption(index)}
              />
            </div>
          ))}
        </div>
      )}

      {kit.graphics.length === 0 && noGraphics}
      {kit.graphics.length > 0 && (
        <div className="flex flex-col gap-8" {...testIdProp(ids.graphics)}>
          <span className="text-s13 font-w600 text-text-strong">{COPY.graphics}</span>
          <div className="flex flex-wrap items-center justify-between gap-x-12 gap-y-8">
            <ul className="flex min-w-0 flex-wrap gap-8">
              {kit.graphics.map((graphic) => (
                <li key={graphic.id}>
                  <a
                    href={graphic.url}
                    download={graphic.file_name}
                    title={`${COPY.download} ${graphic.title}`}
                    aria-label={`${COPY.download} ${graphic.title}`}
                    onClick={() => onShared?.('download_graphics')}
                    className="relative block size-[56px] overflow-hidden rounded-md border border-border-subtle"
                    {...testIdProp(ids.graphic(graphic.id))}
                  >
                    <img src={graphic.url} alt="" className="size-full object-cover" />
                    <span
                      aria-hidden
                      className="absolute right-2 bottom-2 flex size-20 items-center justify-center rounded-sm bg-surface text-icon-muted shadow-sm"
                    >
                      <Icon icon={Download} size={12} />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              className="shrink-0"
              leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
              loading={busy === 'graphics'}
              disabled={busy !== null && busy !== 'graphics'}
              onClick={onDownloadGraphics}
              testId={ids.downloadGraphics}
            >
              {kit.graphics.length === 1 ? COPY.download : COPY.downloadAll}
            </Button>
          </div>
        </div>
      )}

      {kit.email && (
        <div className="flex flex-col items-start gap-4">
          <a
            href={shareKitMailto(kit.email)}
            onClick={() => onShared?.('email')}
            className={cn(saasButtonVariants({ tone: 'secondary', size: 'md' }), 'max-lg:min-h-44')}
            {...testIdProp(ids.email)}
          >
            <Icon icon={Mail} size={14} aria-hidden />
            {COPY.email}
          </a>
          <p className="text-s12 text-text-secondary" {...testIdProp(ids.emailHint)}>
            {COPY.emailHint}
          </p>
        </div>
      )}
    </div>
  );
}

function CopyButton({
  label,
  onClick,
  testId,
}: {
  label: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <IconButton
      aria-label={label}
      title={label}
      onClick={onClick}
      className="max-lg:size-44"
      testId={testId}
    >
      <Icon icon={Copy} size={16} aria-hidden />
    </IconButton>
  );
}
